import * as React from 'react';
import { useIntl } from 'react-intl';

import { Product as SchemaProduct } from 'schema-dts';

import { JsonLd } from '@components/JsonLd';
import { htmlToPlainText } from '@components/JsonLd/serialize';

import { Product } from '../../model';
import { useProductDetailContext } from '../../ProductDetailContext';
import { useSsrProductUrl } from '../hooks/useSsrProductUrl';

import { useVariantOffers } from './useVariantOffers';
import { getImagesSrc, getAggregateRating } from './utils';

interface IProductJsonLdProps {
  product: Product;
  selectedVariant: Product['variants'][number];
  imageDomainPath: string;
}

export const ProductJsonLd = React.memo(
  ({ product, selectedVariant, imageDomainPath }: IProductJsonLdProps) => {
    const productUrl = useSsrProductUrl(product).url;

    const { formatMessage } = useIntl();

    const { shopSettings } = useProductDetailContext();

    const countryCode = shopSettings?.countryCode || '';

    const variantOffers = useVariantOffers({
      formatMessage,
      imageDomainPath,
      itemCountry: countryCode,
      product,
    });

    return (
      <JsonLd<SchemaProduct>
        item={{
          '@context': 'https://schema.org',
          '@type': 'Product',
          '@id': productUrl ?? undefined,
          sku: product.masterProductCode,
          gtin13: selectedVariant.eanCode,
          category: product.annotation ?? undefined,
          name: `${product.brand?.name ?? ''} ${product.collection ?? ''}`,
          description: product.description
            ? htmlToPlainText(product.description)
            : undefined,
          brand: {
            '@type': 'Brand',
            name: product.brand?.name ?? '',
          },
          image: getImagesSrc(selectedVariant, imageDomainPath),
          offers: variantOffers,
          ...(product.reviewOverview &&
            product.reviewOverview.count > 0 && {
              aggregateRating: getAggregateRating(product.reviewOverview),
            }),
        }}
      />
    );
  }
);
